import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    calculateCenterPosition,
    calculateMaxImgSizeToFitContainer,
} from '../../helpers/imageHelpers';
import {
    createFabricCanvas,
    createFabricImageObjectFromSrc,
    createFabricTextObject,
    deleteFabricCanvas,
} from '../../helpers/fabricJsHelpers';
import Isvg from 'react-inlinesvg';
import settings from '../../settings';

class TransformCanvas extends React.Component {
    constructor() {
        super();
        this.lib3d = b4w.require('Library3D');
        this.canvas = undefined;
        this.hiddenCanvas = undefined;
        this.object = undefined;
        this.hiddenObject = undefined;
        this.ratio = 3;
        this.hiddenRatio = 1;
        this.rotateStep = 0;
        this.tempScalingProps = { left: 0, top: 0, scaleX: 0, scaleY: 0 };
        this.borderReached = false;
        this.initCanvas = this.initCanvas.bind(this);
        this.addImage = this.addImage.bind(this);
        this.addHiddenImage = this.addHiddenImage.bind(this);
        this.addText = this.addText.bind(this);
        this.addHiddenText = this.addHiddenText.bind(this);
        this.handleModification = this.handleModification.bind(this);
        this.handleRotate = this.handleRotate.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.applyTransformationToPenCanvas = this.applyTransformationToPenCanvas.bind(this);
        this.setTransformationToState = this.setTransformationToState.bind(this);
        this.setTempScalingProps = this.setTempScalingProps.bind(this);
        this.resetTempScalingProps = this.resetTempScalingProps.bind(this);
        this.transformObject = this.transformObject.bind(this);
        this.transformHiddenObject = this.transformHiddenObject.bind(this);
        this.getScaledDownTransformation = this.getScaledDownTransformation.bind(this);
        this.preventObjectScalingOutsideOfCanvasBoundaries = this.preventObjectScalingOutsideOfCanvasBoundaries.bind(
            this
        );
        this.preventObjectDeselection = this.preventObjectDeselection.bind(this);
        this.getObjectControlStyleSettings = this.getObjectControlStyleSettings.bind(this);
        this.getTextObjectStyleSettings = this.getTextObjectStyleSettings.bind(this);
        this.resetAndReinit = this.resetAndReinit.bind(this);
        this.drawObjectAndRender = this.drawObjectAndRender.bind(this);
        this.deleteAreaConfig = this.deleteAreaConfig.bind(this);
        this.keepObjectWithinCanvasBoundaries = this.keepObjectWithinCanvasBoundaries.bind(this);
    }

    componentDidMount() {
        this.initCanvas();
        this.drawObjectAndRender();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.image !== this.props.image ||
            prevProps.text !== this.props.text ||
            prevProps.container !== this.props.container
        ) {
            this.deleteAreaConfig();
            this.resetAndReinit();
            return;
        }

        this.transformObject();
        this.transformHiddenObject();
        this.canvas.renderAll();
        this.hiddenCanvas.renderAll();
    }

    initCanvas() {
        const {
            container: { width, height },
        } = this.props;

        this.canvas = createFabricCanvas(this.props.id, width / this.ratio, height / this.ratio);
        this.hiddenCanvas = createFabricCanvas('hidden-canvas', width, height);

        this.canvas.selection = false;

        this.canvas.on('object:moving', this.keepObjectWithinCanvasBoundaries);
        this.canvas.on('object:scaling', this.preventObjectScalingOutsideOfCanvasBoundaries);
        this.canvas.on('selection:cleared', this.preventObjectDeselection);
    }

    drawObjectAndRender() {
        let promises;

        if (this.props.image) {
            promises = [this.addImage(), this.addHiddenImage()];
        }

        if (this.props.text) {
            promises = [this.addText(), this.addHiddenText()];
        }

        // if there are promises returned, wait until they are resolved and then
        // render all canvases
        if (promises) {
            Promise.all(promises).then(() => {
                this.canvas.setActiveObject(this.canvas.item(0));
                this.canvas.renderAll();
                this.hiddenCanvas.renderAll();
                this.applyTransformationToPenCanvas();
            });
        } else {
            console.error('No image or text defined in TransformCanvas');
        }
    }

    resetAndReinit() {
        deleteFabricCanvas(this.canvas);
        deleteFabricCanvas(this.hiddenCanvas);
        this.rotateStep = 0;
        this.resetTempScalingProps();
        this.initCanvas();
        this.drawObjectAndRender();
    }

    deleteAreaConfig() {
        const { config, container, setConfig, containerType } = this.props;
        const newConfig = _.cloneDeep(config);

        if (containerType === 'printable-area') {
            delete newConfig.printableAreas[container.name];
        }

        if (containerType === 'engraving-area') {
            delete newConfig.engravingAreas[container.name];
        }
        setConfig(newConfig);
    }

    setTransformationToState(width, height, x, y, rotation) {
        const {
            config,
            container,
            setConfig,
            image,
            text,
            customizationType,
            containerType,
        } = this.props;
        const newConfig = _.cloneDeep(config);

        const transformation = {
            type: customizationType,
            transformation: { width, height, x, y, rotation },
            image: image ? image.id : null,
            text: text ? text.id : null,
        };

        if (containerType === 'printable-area') {
            newConfig.printableAreas[container.name] = transformation;
        }

        if (containerType === 'engraving-area') {
            newConfig.engravingAreas[container.name] = transformation;
        }

        setConfig(newConfig);
    }

    getScaledDownTransformation() {
        const { width, height, x, y, rotation } = this.getTransformation();

        return {
            scaledWidth: width / this.ratio,
            scaledHeight: height / this.ratio,
            scaledX: x / this.ratio,
            scaledY: y / this.ratio,
            rotation,
        };
    }

    addImage() {
        const { image, containerType } = this.props;
        const {
            scaledWidth,
            scaledHeight,
            scaledX,
            scaledY,
            rotation,
        } = this.getScaledDownTransformation();
        // use brown filtered image src if containerType is not printable-area => is engraving-area
        const imageSrc = containerType === 'printable-area' ? image.src : image.filteredSrc;

        return new Promise((resolve, reject) => {
            const options = {
                originX: 'center',
                originY: 'center',
                left: scaledX,
                top: scaledY,
                hasControls: true,
                lockScalingFlip: true,
                snapAngle: 90,
                unselectable: true,
                lockUniScaling: true,
                angle: rotation,
                ...this.getObjectControlStyleSettings(),
            };
            createFabricImageObjectFromSrc(imageSrc, options).then(imageObject => {
                imageObject.scaleToWidth(scaledWidth);
                imageObject.scaleToHeight(scaledHeight);
                imageObject.on('modified', this.handleModification);

                this.object = imageObject;
                this.canvas.add(imageObject);
                resolve();
            });
        });
    }

    addHiddenImage() {
        const { image, containerType } = this.props;
        const { width, height, x, y, rotation } = this.getTransformation();
        // use brown filtered image src if containerType is not printable-area => is engraving-area
        const imageSrc = containerType === 'printable-area' ? image.src : image.filteredSrc;

        return new Promise((resolve, reject) => {
            const options = {
                originX: 'center',
                originY: 'center',
                left: x,
                top: y,
                angle: rotation,
                hasControls: false,
                lockScalingFlip: true,
                snapAngle: 90,
                unselectable: true,
                lockUniScaling: true,
            };
            createFabricImageObjectFromSrc(imageSrc, options).then(imageObject => {
                imageObject.scaleToWidth(width);
                imageObject.scaleToHeight(height);
                imageObject.on('modified', this.handleModification);

                this.hiddenObject = imageObject;
                this.hiddenCanvas.add(imageObject);

                // set state after defining this.hiddenObject, since it state update causes a re-render
                this.setTransformationToState(width, height, x, y, 0);
                resolve();
            });
        });
    }

    addText() {
        const { text, container } = this.props;
        const {
            scaledWidth,
            scaledHeight,
            scaledX,
            scaledY,
            rotation,
        } = this.getScaledDownTransformation();

        return new Promise((resolve, reject) => {
            const options = {
                originX: 'center',
                originY: 'center',
                left: scaledX,
                top: scaledY,
                angle: rotation,
                lockScalingFlip: true,
                lockUniScaling: true,
                ...this.getTextObjectStyleSettings(),
                ...this.getObjectControlStyleSettings(),
            };

            createFabricTextObject(text.text, options).then(textObject => {
                textObject.on('modified', this.handleModification);

                this.object = textObject;
                this.canvas.add(textObject);
                resolve();
            });
        });
    }

    addHiddenText() {
        const { text, container } = this.props;
        const { width, height, x, y, rotation } = this.getTransformation();

        return new Promise((resolve, reject) => {
            const options = {
                originX: 'center',
                originY: 'center',
                left: x,
                top: y,
                angle: rotation,
                hasControls: false,
                ...this.getTextObjectStyleSettings(),
            };

            createFabricTextObject(text.text, options).then(textObject => {
                textObject.on('modified', this.handleModification);

                if (width === container.width && height === container.height) {
                    const { width: a, height: b } = calculateMaxImgSizeToFitContainer(
                        textObject.width * textObject.scaleX,
                        textObject.height * textObject.scaleY,
                        container.width,
                        container.height
                    );

                    textObject.scaleToWidth(a);
                    textObject.scaleToHeight(b);
                    textObject.setCoords();

                    this.hiddenObject = textObject;
                    this.hiddenCanvas.add(textObject);

                    this.setTransformationToState(a, b, x, y, rotation);
                } else {
                    this.hiddenObject = textObject;
                    this.hiddenCanvas.add(textObject);
                    // set state after defining this.hiddenObject, since it state update causes a re-render
                    this.setTransformationToState(width, height, x, y, rotation);
                }

                resolve();
            });
        });
    }

    transformObject() {
        const {
            scaledWidth,
            scaledHeight,
            scaledX,
            scaledY,
            rotation,
        } = this.getScaledDownTransformation();
        this.object.scaleToWidth(scaledWidth);
        this.object.scaleToHeight(scaledHeight);
        this.object.set({
            top: scaledY,
            left: scaledX,
            angle: rotation,
        });
    }

    transformHiddenObject() {
        const { width, height, x, y, rotation } = this.getTransformation();
        this.hiddenObject.scaleToWidth(width);
        this.hiddenObject.scaleToHeight(height);
        this.hiddenObject.set({
            top: y,
            left: x,
            angle: rotation,
        });
    }

    getTransformation() {
        const { container, config } = this.props;
        const areas = { ...config.printableAreas, ...config.engravingAreas };

        if (areas.hasOwnProperty(container.name)) {
            return areas[container.name].transformation;
        }

        return this.getInitialTransformation();
    }

    getInitialTransformation() {
        const { container, image } = this.props;
        let width, height;

        if (image) {
            let { width: a, height: b } = calculateMaxImgSizeToFitContainer(
                image.width,
                image.height,
                container.width,
                container.height
            );
            width = a;
            height = b;
        } else {
            width = container.width;
            height = container.height;
        }

        const { x, y } = calculateCenterPosition(container.width, container.height);

        return { width, height, x, y, rotation: 0 };
    }

    handleModification() {
        this.keepObjectWithinCanvasBoundaries();

        const { container } = this.props;
        const { angle, top, left, scaleX, scaleY, height, width } = this.object;

        let scaledWidth = 0;
        let scaledHeight = 0;
        let scaledX = left * this.ratio;
        let scaledY = top * this.ratio;

        if (this.rotateStep % 2 === 1) {
            // rotation is vertical
            // make sure image can fit the container after rotation
            let { width: a, height: b } = calculateMaxImgSizeToFitContainer(
                // swap width and height
                height * scaleX * this.ratio, // width
                width * scaleY * this.ratio, // height
                container.width,
                container.height
            );
            scaledWidth = a;
            scaledHeight = b;
        } else {
            // scale up by ratio
            scaledWidth = width * scaleX * this.ratio;
            scaledHeight = height * scaleY * this.ratio;
        }

        this.setTransformationToState(scaledWidth, scaledHeight, scaledX, scaledY, angle);
        this.applyTransformationToPenCanvas();
    }

    handleRotate() {
        let { rotation } = this.getTransformation();

        rotation += 90;

        let promise = new Promise(resolve => {
            this.object.rotate(rotation);
            this.hiddenObject.rotate(rotation);
            resolve();
        });

        promise.then(() => {
            this.rotateStep++;
            this.handleModification();
            this.applyTransformationToPenCanvas();
        });
    }

    handleDelete() {
        const {
            setActiveImage,
            setActiveText,
            resetTransformArea,
            setConfig,
            config,
            container,
            containerType,
        } = this.props;
        const newConfig = _.cloneDeep(config);

        // clear 3d model canvas
        const context = this.lib3d.getCanvasContext(container.name);
        context.clearRect(0, 0, 1024, 1024);
        this.lib3d.setCanvasContext(container.name, context);

        // unset activeImage
        setActiveImage(null);
        setActiveText(null);

        // reset transformArea
        resetTransformArea();

        // clear configuration
        if (containerType === 'printable-area') {
            delete newConfig.printableAreas[container.name];
        }
        if (containerType === 'engraving-area') {
            delete newConfig.engravingAreas[container.name];
        }

        setConfig(newConfig);
    }

    applyTransformationToPenCanvas() {
        const { container } = this.props;
        const context = this.lib3d.getCanvasContext(container.name);

        context.clearRect(0, 0, 1024, 1024);

        context.drawImage(
            document.getElementById('hidden-canvas'),
            0, // x
            (1024 - container.height) / 2, // y
            container.width,
            container.height
        );

        this.lib3d.setCanvasContext(container.name, context);
    }

    setTempScalingProps(left, top, scaleX, scaleY) {
        this.tempScalingProps['left'] = left;
        this.tempScalingProps['top'] = top;
        this.tempScalingProps['scaleX'] = scaleX;
        this.tempScalingProps['scaleY'] = scaleY;
    }

    resetTempScalingProps() {
        this.tempScalingProps = { left: 0, top: 0, scaleX: 0, scaleY: 0 };
    }

    preventObjectScalingOutsideOfCanvasBoundaries() {
        const obj = this.object;

        const brOld = obj.getBoundingRect();
        obj.setCoords();
        const brNew = obj.getBoundingRect();

        // left border
        if (brOld.left >= 0 && brNew.left < 0 && !this.borderReached) {
            let scale = (brOld.width + brOld.left) / obj.width;
            let left = obj.left;
            let top = obj.top;
            this.setTempScalingProps(left, top, scale, scale);
            this.borderReached = true;
        }
        // top border
        if (brOld.top >= 0 && brNew.top < 0 && !this.borderReached) {
            let scale = (brOld.height + brOld.top) / obj.height;
            let left = obj.left;
            let top = obj.top;
            this.setTempScalingProps(left, top, scale, scale);
            this.borderReached = true;
        }
        // right border
        if (
            brOld.left + brOld.width <= obj.canvas.width &&
            brNew.left + brNew.width > obj.canvas.width &&
            !this.borderReached
        ) {
            let scale = (obj.canvas.width - brOld.left) / obj.width;
            let left = obj.left;
            let top = obj.top;
            this.setTempScalingProps(left, top, scale, scale);
            this.borderReached = true;
        }
        // bottom border
        if (
            brOld.top + brOld.height <= obj.canvas.height &&
            brNew.top + brNew.height > obj.canvas.height &&
            !this.borderReached
        ) {
            let scale = (obj.canvas.height - brOld.top) / obj.height;
            let left = obj.left;
            let top = obj.top;
            this.setTempScalingProps(left, top, scale, scale);
            this.borderReached = true;
        }

        if (
            brNew.left < -1 ||
            brNew.top < -1 ||
            brNew.left + brNew.width > obj.canvas.width ||
            brNew.top + brNew.height > obj.canvas.height
        ) {
            obj.left = this.tempScalingProps['left'];
            obj.top = this.tempScalingProps['top'];
            obj.scaleX = this.tempScalingProps['scaleX'];
            obj.scaleY = this.tempScalingProps['scaleY'];

            obj.setCoords();
        } else {
            this.borderReached = false;
        }
    }

    preventObjectDeselection() {
        this.canvas.setActiveObject(this.object);
    }

    getObjectControlStyleSettings() {
        const { lightblue } = settings.colors;
        return {
            // hasBorders: false,
            hasRotatingPoint: false,
            cornerStyle: 'circle',
            cornerColor: lightblue,
            cornerStrokeColor: lightblue,
            borderColor: lightblue,
            transparentCorners: false,
        };
    }

    getTextObjectStyleSettings() {
        const { text, containerType } = this.props;
        const { brown } = settings.colors;
        return {
            fontFamily: text.font,
            fontWeight: text.bold ? 'bold' : 'normal',
            fontStyle: text.italic ? 'italic' : 'normal',
            textAlign: text.alignment || 'left',
            fill:
                containerType === 'printable-area'
                    ? `rgb(${text.color.r}, ${text.color.g}, ${text.color.b})`
                    : brown,
        };
    }

    keepObjectWithinCanvasBoundaries() {
        const obj = this.object;

        obj.setCoords();

        // top-left  corner
        if (obj.getBoundingRect().top < 0 || obj.getBoundingRect().left < 0) {
            obj.top = Math.max(obj.top, obj.top - obj.getBoundingRect().top);
            obj.left = Math.max(obj.left, obj.left - obj.getBoundingRect().left);
        }

        // bot-right corner
        if (
            obj.getBoundingRect().top + obj.getBoundingRect().height > obj.canvas.height ||
            obj.getBoundingRect().left + obj.getBoundingRect().width > obj.canvas.width
        ) {
            obj.top = Math.min(
                obj.top,
                obj.canvas.height -
                    obj.getBoundingRect().height +
                    obj.top -
                    obj.getBoundingRect().top
            );
            obj.left = Math.min(
                obj.left,
                obj.canvas.width -
                    obj.getBoundingRect().width +
                    obj.left -
                    obj.getBoundingRect().left
            );
        }
    }

    render() {
        const classes = ['transform-canvas', ...this.props.classes];
        const { backgroundColor } = this.props;
        const bgRGB = `rgb(${backgroundColor.r}, ${backgroundColor.g}, ${backgroundColor.b})`;
        const style = {
            backgroundColor: bgRGB,
            backgroundImage: `linear-gradient(-180deg, ${bgRGB} 0%, rgba(0,0,0,0.25) 100%)`,
        };
        return (
            <div className={classes.join(' ')}>
                <div className="transform-canvas-controls">
                    <div className="rotate" onClick={this.handleRotate}>
                        <Isvg src="/svg/rotate.svg" />
                    </div>
                    <div className="remove" onClick={this.handleDelete}>
                        <Isvg src="/svg/cancel.svg" />
                    </div>
                </div>
                <div style={style}>
                    <canvas id={this.props.id} />
                </div>
                <div style={{ display: 'none', border: '1px solid black' }}>
                    <canvas id="hidden-canvas" />
                </div>
            </div>
        );
    }
}

TransformCanvas.propTypes = {
    classes: PropTypes.arrayOf(PropTypes.string),
    id: PropTypes.string.isRequired,
    image: PropTypes.object,
    container: PropTypes.object,
};

TransformCanvas.defaultProps = {
    classes: [],
};

export default TransformCanvas;
